
  import { defineComponent, PropType } from 'vue';
  import { Cell, ColumnHelper, createColumnHelper } from '@tanstack/vue-table';

  import TableAggregated from '@/common/components/redesigned/Table/TableAggregated.vue';
  import {
    TPaginationConfig,
    TTableAggregatedProgressBarOptions,
  } from '@/common/components/redesigned/Table/types/tableTypes';
  import text from '@/common/mixins/text';

  import { PAGINATION_CONFIG } from '../../constants/pagination';
  import { TABLE_AGGREGATED_PROGRESS_BAR_OPTIONS } from '../../constants/tableAggregatedProgress';
  import {
    FOREIGN_TRADE_BY_REGIONS_ACTIONS_NAME,
    FOREIGN_TRADE_BY_REGIONS_GETTERS_NAME,
    FOREIGN_TRADE_BY_REGIONS_MUTATIONS_NAME,
  } from '../../store/foreignTradeByRegions/name';
  import { IndicatorTypes } from '../../types/common-aggregated';
  import {
    TForeignTradeByRegionsFilter,
    TForeignTradeByRegionsItem,
    TForeignTradeByRegionsTableData,
  } from '../../types/foreign-trade-by-regions';

  export default defineComponent({
    name: 'ForeignTradeByRegionsTable',
    components: {
      TableAggregated,
    },
    mixins: [text],
    props: {
      filter: {
        type: Object as PropType<TForeignTradeByRegionsFilter>,
        required: true,
      },
    },
    data() {
      return {
        storePaths: {
          action: FOREIGN_TRADE_BY_REGIONS_ACTIONS_NAME.searchRegionAggregated,
          getter: FOREIGN_TRADE_BY_REGIONS_GETTERS_NAME.getRegionAggregated,
          mutation: FOREIGN_TRADE_BY_REGIONS_MUTATIONS_NAME.setRegionAggregated,
        },
      };
    },
    computed: {
      getPaginationConfig(): TPaginationConfig {
        return PAGINATION_CONFIG;
      },
      getProgressBarOptions(): TTableAggregatedProgressBarOptions {
        return TABLE_AGGREGATED_PROGRESS_BAR_OPTIONS;
      },
    },
    methods: {
      generateColumns(data: TForeignTradeByRegionsTableData) {
        if (!data) return [];

        const columnHelper = createColumnHelper<TForeignTradeByRegionsItem>();

        return [
          // Поставил игнорирования из-за того, что в браузере вылезает ошибка типа (после обновления ts должна уйти)
          // @ts-ignore
          columnHelper.accessor('name', {
            header: 'Наименование субъекта РФ',
            size: 400,
            meta: {
              fixed: true,
            },
          }),
          ...(this.getTableGroup(data, columnHelper) ?? []),
        ];
      },
      getTableGroup(data: TForeignTradeByRegionsTableData, columnHelper: ColumnHelper<TForeignTradeByRegionsItem>) {
        switch (this.filter.indicatorTypeId) {
          case IndicatorTypes.PRICE_AND_WEIGHT:
            return this.getTableGroupByPriceAndWeight(data, columnHelper);
          case IndicatorTypes.PRICE:
            return this.getTableGroupByPrice(data, columnHelper);
          case IndicatorTypes.WEIGHT:
            return this.getTableGroupByWeight(data, columnHelper);
        }
      },
      getTableGroupByPriceAndWeight(
        data: TForeignTradeByRegionsTableData,
        columnHelper: ColumnHelper<TForeignTradeByRegionsItem>,
      ) {
        return [
          ...data.periods.map((period, index) => {
            return columnHelper.group({
              header: period,
              id: `${period}${index}`,
              columns: [
                columnHelper.group({
                  header: 'Экспорт',
                  columns: [
                    columnHelper.accessor(
                      (row: TForeignTradeByRegionsItem) => this.formatNumber(row.indicators[index]?.export.weight, 1),
                      {
                        header: 'Вес, тонн',
                        id: `export-weight-${index}`,
                        size: 160,
                      },
                    ),
                    columnHelper.accessor(
                      (row: TForeignTradeByRegionsItem) => this.formatNumber(row.indicators[index]?.export.price, 1),
                      {
                        header: 'Стоимость, тыс. долл.',
                        id: `export-price-${index}`,
                        size: 160,
                      },
                    ),
                  ],
                }),
                columnHelper.group({
                  header: 'Импорт',
                  columns: [
                    columnHelper.accessor(
                      (row: TForeignTradeByRegionsItem) => this.formatNumber(row.indicators[index]?.import.weight, 1),
                      {
                        header: 'Вес, тонн',
                        id: `import-weight-${index}`,
                        size: 160,
                      },
                    ),
                    columnHelper.accessor(
                      (row: TForeignTradeByRegionsItem) => this.formatNumber(row.indicators[index]?.import.price, 1),
                      {
                        header: 'Стоимость, тыс. долл.',
                        id: `import-price-${index}`,
                        size: 160,
                      },
                    ),
                  ],
                }),
              ],
            });
          }),
        ];
      },
      getTableGroupByPrice(
        data: TForeignTradeByRegionsTableData,
        columnHelper: ColumnHelper<TForeignTradeByRegionsItem>,
      ) {
        return [
          {
            header: 'Стоимость, тыс. долл.',
            id: 'price-only',
            columns: [
              ...data.periods.map((period, index) => {
                return columnHelper.group({
                  header: period,
                  id: `${period}${index}`,
                  columns: [
                    columnHelper.accessor(
                      (row: TForeignTradeByRegionsItem) => this.formatNumber(row.indicators[index]?.export.price, 1),
                      {
                        header: 'Экспорт',
                        id: `export-price-only-${index}`,
                        size: 160,
                      },
                    ),
                    columnHelper.accessor(
                      (row: TForeignTradeByRegionsItem) => this.formatNumber(row.indicators[index]?.import.price, 1),
                      {
                        header: 'Импорт',
                        id: `import-price-only-${index}`,
                        size: 160,
                      },
                    ),
                  ],
                });
              }),
            ],
          },
          {
            header: 'Доля в % к итогу',
            id: 'price-only-percent',
            columns: [
              ...data.periods.map((period, index) => {
                return columnHelper.group({
                  header: period,
                  id: `${period}${index}`,
                  columns: [
                    columnHelper.accessor(
                      (row: TForeignTradeByRegionsItem) =>
                        this.formatNumber(row.indicators[index]?.export.total.price, 1),
                      {
                        header: 'Экспорт',
                        id: `export-price-percent-only-${index}`,
                        size: 160,
                      },
                    ),
                    columnHelper.accessor(
                      (row: TForeignTradeByRegionsItem) =>
                        this.formatNumber(row.indicators[index]?.import.total.price, 1),
                      {
                        header: 'Импорт',
                        id: `import-price-percent-only-${index}`,
                        size: 160,
                      },
                    ),
                  ],
                });
              }),
            ],
          },
        ];
      },
      getTableGroupByWeight(
        data: TForeignTradeByRegionsTableData,
        columnHelper: ColumnHelper<TForeignTradeByRegionsItem>,
      ) {
        return [
          {
            header: 'Вес, тонн',
            id: `weight-only`,
            columns: [
              ...data.periods.map((period, index) => {
                return columnHelper.group({
                  header: period,
                  id: `${period}${index}`,
                  columns: [
                    columnHelper.accessor(
                      (row: TForeignTradeByRegionsItem) => this.formatNumber(row.indicators[index]?.export.weight, 1),
                      {
                        header: 'Экспорт',
                        id: `export-weight-only-${index}`,
                        size: 160,
                      },
                    ),
                    columnHelper.accessor(
                      (row: TForeignTradeByRegionsItem) => this.formatNumber(row.indicators[index]?.import.weight, 1),
                      {
                        header: 'Импорт',
                        id: `import-weight-only-${index}`,
                        size: 160,
                      },
                    ),
                  ],
                });
              }),
            ],
          },
          {
            header: 'Доля в % к итогу',
            id: 'weight-only-percent',
            columns: [
              ...data.periods.map((period, index) => {
                return columnHelper.group({
                  header: period,
                  id: `${period}${index}`,
                  columns: [
                    columnHelper.accessor(
                      (row: TForeignTradeByRegionsItem) =>
                        this.formatNumber(row.indicators[index]?.export.total.weight, 1),
                      {
                        header: 'Экспорт',
                        id: `export-weight-percent-only-${index}`,
                        size: 160,
                      },
                    ),
                    columnHelper.accessor(
                      (row: TForeignTradeByRegionsItem) =>
                        this.formatNumber(row.indicators[index]?.import.total.weight, 1),
                      {
                        header: 'Импорт',
                        id: `import-weight-percent-only-${index}`,
                        size: 160,
                      },
                    ),
                  ],
                });
              }),
            ],
          },
        ];
      },
      rowClassGetter(row: TForeignTradeByRegionsItem) {
        return `foreign-trade-by-regions-table__row--level-${row.level}`;
      },
      cellClassGetter(cell: Cell<TForeignTradeByRegionsItem, any>) {
        return cell.id.includes('name')
          ? `foreign-trade-by-regions-table__cell--name foreign-trade-by-regions-table__cell--name-${cell.row.original.level}`
          : '';
      },
    },
  });
